import { PHOTO_KIND_NAMES, type PhotoKind } from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useRef, useState } from 'react';

import { addPhoto, listByReport, preparePhoto, retryPhoto } from '../photos/index.js';
import type { PhotoRecord } from '../photos/index.js';
import { runPhotos } from '../sync/engine.js';

/**
 * Съёмка рабочего места.
 *
 * Снимок делается прямо из приложения, сжимается и штампуется на устройстве,
 * после чего ложится в очередь. Оператор не ждёт загрузку: как только снимок
 * в очереди, шаг считается выполненным.
 */

export interface PhotoCaptureProps {
  reportId: string;
  /** Что снимаем: продукцию или переданное рабочее место. */
  kind: PhotoKind;
  siteId: string;
  siteName: string;
  shiftNumber: number;
  machineId?: string | null;
  machineName?: string | undefined;
}

const STATUS_LABEL: Record<PhotoRecord['status'], string> = {
  pending: 'Ожидает загрузки',
  uploading: 'Загружается',
  uploaded: 'Загружено',
  failed: 'Ошибка загрузки',
};

const STATUS_TONE: Record<PhotoRecord['status'], string> = {
  pending: 'bg-amber-500 text-black',
  uploading: 'bg-sky-600 text-white',
  uploaded: 'bg-emerald-700 text-white',
  failed: 'bg-red-600 text-white',
};

function PhotoRow({ photo }: { photo: PhotoRecord }): JSX.Element {
  const percent =
    photo.totalChunks > 0 ? Math.round((photo.uploadedChunks / photo.totalChunks) * 100) : 0;

  return (
    <li className="rounded-xl bg-muted p-4">
      <div className="flex flex-wrap items-center justify-between gap-touch">
        <span className={`rounded-lg px-3 py-1 text-base font-semibold ${STATUS_TONE[photo.status]}`}>
          {STATUS_LABEL[photo.status]}
        </span>
        <span className="text-base text-dim">{Math.round(photo.bytes / 1024)} КБ</span>
      </div>

      {photo.status === 'uploading' && (
        <p className="mt-2 text-base text-body">
          Отправлено {photo.uploadedChunks} из {photo.totalChunks} частей ({percent}%)
        </p>
      )}
      {photo.lastError && <p className="mt-2 text-base text-red-300">{photo.lastError}</p>}

      {photo.status === 'failed' && (
        <button
          type="button"
          onClick={() => void retryPhoto(photo.id).then(() => runPhotos())}
          className="mt-3 rounded-xl bg-sky-600 px-5 py-3 text-base font-semibold text-white active:bg-sky-700"
        >
          Повторить
        </button>
      )}
    </li>
  );
}

export function PhotoCapture(props: PhotoCaptureProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Каждый блок показывает только свои снимки: продукция и рабочее место
  // сдаются раздельно, и путать их нельзя
  const all = useLiveQuery(() => listByReport(props.reportId), [props.reportId], [] as PhotoRecord[]);
  const photos = all.filter((photo) => photo.kind === props.kind);

  const handleFile = async (file: File): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const takenAt = new Date();
      const prepared = await preparePhoto(file, {
        takenAt,
        siteName: props.siteName,
        shiftNumber: props.shiftNumber,
        machineName: props.machineName,
      });
      await addPhoto({
        reportId: props.reportId,
        kind: props.kind,
        siteId: props.siteId,
        machineId: props.machineId ?? null,
        shiftNumber: props.shiftNumber,
        takenAt,
        prepared,
      });
      if (navigator.vibrate) navigator.vibrate(60);
      void runPhotos();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось подготовить снимок');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />

      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="w-full rounded-xl bg-sky-600 px-5 py-4 text-lg font-semibold text-white active:bg-sky-700 disabled:opacity-50"
      >
        {busy ? 'Готовлю снимок…' : `Снять: ${PHOTO_KIND_NAMES[props.kind].toLowerCase()}`}
      </button>

      {error && <p className="mt-3 text-base text-red-300">{error}</p>}

      {photos.length === 0 ? (
        <p className="mt-3 text-base text-dim">Фото не прикреплено</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-touch">
          {photos.map((photo) => (
            <PhotoRow key={photo.id} photo={photo} />
          ))}
        </ul>
      )}
    </div>
  );
}
